// Notification preferences (2026-10-18) — one source of truth for onboarding Step 9 and the Profile section.
//
// Two separate things, never mixed:
//   - PREFERENCES: server-side, per user, all devices  -> GET/PUT /notification-preferences
//       enabled (master) · training (today: race-morning push only) · coach_analysis (Garmin analysis push —
//       sent only while the server's GARMIN_PUSH_ENABLED is on — and the Strava analysis pushes)
//   - DEVICE: the browser / OS permission and whether THIS device's token is registered. Only the device knows its
//     permission; only the server knows the registration.
// Nothing here may claim notifications will reach a device unless permission is granted AND the device is registered
// AND the preference is on. A failed save is an error, never consent.
import authFetch from './authFetch';

export const PREF_KEYS = ['enabled', 'training', 'coach_analysis'];

/** 'granted' | 'denied' | 'default' | 'unsupported' | 'ios_needs_install' */
export function devicePermissionState(env = typeof window !== 'undefined' ? window : undefined) {
  if (!env) return 'unsupported';
  const nav = env.navigator || {};
  const ua = nav.userAgent || '';
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  const standalone = nav.standalone === true
    || (typeof env.matchMedia === 'function' && env.matchMedia('(display-mode: standalone)').matches);
  if (isIOS && !standalone) return 'ios_needs_install';     // iOS web push only from the Home Screen app
  if (!('Notification' in env) || !('serviceWorker' in nav)) return 'unsupported';
  const p = env.Notification.permission;
  return p === 'granted' || p === 'denied' ? p : 'default';
}

/** Onboarding choice -> the exact preferences to persist. Nothing selected ("Not now") = everything off. */
export function onboardingChoice({ training = false, coachAnalysis = false } = {}) {
  const t = training === true;
  const c = coachAnalysis === true;
  return { enabled: t || c, training: t, coach_analysis: c };
}

/** Will notifications of this category reach THIS device? Only when every condition holds. */
export function willReachThisDevice({ permission, thisDeviceRegistered, prefs, category }) {
  if (permission !== 'granted' || !thisDeviceRegistered || !prefs || !prefs.enabled) return false;
  return category ? Boolean(prefs[category]) : true;
}

export async function fetchNotificationPrefs(apiBase, userId, fcmToken) {
  const qs = new URLSearchParams({ user_id: userId });
  if (fcmToken) qs.set('fcm_token', fcmToken);
  const res = await authFetch(`${apiBase}/notification-preferences?${qs.toString()}`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

/** PUT and confirm by read-back; throws unless the server stored exactly what was asked. */
export async function saveNotificationPrefs(apiBase, userId, changes, fcmToken) {
  const body = { user_id: userId };
  PREF_KEYS.forEach((k) => { if (typeof changes[k] === 'boolean') body[k] = changes[k]; });
  const qs = fcmToken ? `?fcm_token=${encodeURIComponent(fcmToken)}` : '';
  const res = await authFetch(`${apiBase}/notification-preferences${qs}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const view = await res.json();
  const stored = view && view.preferences;
  const matches = stored && PREF_KEYS.every((k) => typeof body[k] !== 'boolean' || stored[k] === body[k]);
  if (!view.saved || !matches) throw new Error('not_saved');
  try {   // lets the home banner stay quiet for someone who explicitly turned notifications off
    if (stored.enabled === false) localStorage.setItem(`notif-declined-${userId}`, 'true');
    else localStorage.removeItem(`notif-declined-${userId}`);
  } catch { /* storage unavailable */ }
  return view;
}
